import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MapStylePicker } from "@/components/map/MapStylePicker";
import { useMapInit } from "@/hooks/useMapInit";
import type { RouteStop } from "@/lib/reseller-api";
import type { LngLat } from "@/lib/routing";

export type StopState = "pending" | "visited" | "current";

interface RouteMapProps {
  stops: RouteStop[];
  stopStates?: StopState[];
  plannedPath?: LngLat[] | null;
  actualTrack?: LngLat[];
  vehiclePosition?: LngLat | null;
  onMapClick?: (lngLat: LngLat) => void;
  onStopClick?: (index: number) => void;
  /** Change this value to refit the map to the route. */
  fitKey?: string | number;
}

const STOP_COLORS: Record<StopState, string> = {
  pending: "#3b82f6",
  current: "#f59e0b",
  visited: "#16a34a",
};

const emptyCollection: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
const lineFeature = (coordinates: LngLat[]): GeoJSON.FeatureCollection =>
  coordinates.length < 2
    ? emptyCollection
    : { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates } }] };

export const RouteMap = ({
  stops,
  stopStates,
  plannedPath,
  actualTrack = [],
  vehiclePosition,
  onMapClick,
  onStopClick,
  fitKey,
}: RouteMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const [layersReady, setLayersReady] = useState(false);
  const markers = useRef<maplibregl.Marker[]>([]);
  const vehicleMarker = useRef<maplibregl.Marker | null>(null);
  const onMapClickRef = useRef(onMapClick);
  const onStopClickRef = useRef(onStopClick);
  onMapClickRef.current = onMapClick;
  onStopClickRef.current = onStopClick;

  const { map, currentStyle, handleStyleChange } = useMapInit(mapContainer, {
    onBeforeStyleChange: () => setLayersReady(false),
  });

  useEffect(() => {
    if (!map.current || !mapContainer.current) return;
    const resizeObserver = new ResizeObserver(() => map.current?.resize());
    resizeObserver.observe(mapContainer.current);

    map.current.on("styledata", () => {
      if (!map.current || map.current.getSource("planned-path")) return;
      map.current.addSource("planned-path", { type: "geojson", data: emptyCollection });
      map.current.addSource("actual-track", { type: "geojson", data: emptyCollection });
      map.current.addLayer({
        id: "planned-path",
        type: "line",
        source: "planned-path",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#3b82f6", "line-width": 5, "line-opacity": 0.6 },
      });
      map.current.addLayer({
        id: "actual-track",
        type: "line",
        source: "actual-track",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#16a34a", "line-width": 3, "line-dasharray": [1, 1.5] },
      });
      setLayersReady(true);
    });

    map.current.on("click", (event) => onMapClickRef.current?.([event.lngLat.lng, event.lngLat.lat]));

    return () => resizeObserver.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (map.current) map.current.getCanvas().style.cursor = onMapClick ? "crosshair" : "";
  }, [map, onMapClick]);

  useEffect(() => {
    if (!map.current || !layersReady) return;
    (map.current.getSource("planned-path") as maplibregl.GeoJSONSource | undefined)?.setData(lineFeature(plannedPath ?? []));
    (map.current.getSource("actual-track") as maplibregl.GeoJSONSource | undefined)?.setData(lineFeature(actualTrack));
  }, [map, plannedPath, actualTrack, layersReady]);

  // Numbered stop markers (HTML markers don't depend on the style having glyphs).
  useEffect(() => {
    if (!map.current) return;
    markers.current.forEach((marker) => marker.remove());
    markers.current = stops.map((stop, index) => {
      const state = stopStates?.[index] ?? "pending";
      const element = document.createElement("button");
      element.type = "button";
      element.textContent = String(index + 1);
      element.title = stop.name;
      element.style.cssText = `width:26px;height:26px;border-radius:9999px;border:2px solid white;color:white;font-size:12px;font-weight:600;box-shadow:0 1px 4px rgba(0,0,0,.4);cursor:pointer;background:${STOP_COLORS[state]}`;
      element.addEventListener("click", (event) => {
        event.stopPropagation();
        onStopClickRef.current?.(index);
      });
      return new maplibregl.Marker({ element }).setLngLat([stop.longitude, stop.latitude]).addTo(map.current!);
    });
  }, [map, stops, stopStates]);

  useEffect(() => {
    if (!map.current) return;
    vehicleMarker.current?.remove();
    vehicleMarker.current = null;
    if (!vehiclePosition) return;
    const element = document.createElement("div");
    element.style.cssText = "width:16px;height:16px;border-radius:9999px;background:#16a34a;border:3px solid white;box-shadow:0 0 0 4px rgba(22,163,74,.3)";
    vehicleMarker.current = new maplibregl.Marker({ element }).setLngLat(vehiclePosition).addTo(map.current);
  }, [map, vehiclePosition]);

  // Fit to the route when asked (e.g. a route was loaded), not on every edit.
  useEffect(() => {
    if (!map.current) return;
    const points: LngLat[] = [...stops.map((stop) => [stop.longitude, stop.latitude] as LngLat), ...(plannedPath ?? [])];
    if (vehiclePosition) points.push(vehiclePosition);
    if (!points.length) return;
    const bounds = points.reduce((b, point) => b.extend(point), new maplibregl.LngLatBounds(points[0], points[0]));
    map.current.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 0 });
  }, [fitKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainer} className="w-full h-full" />
      <div className="absolute top-3 left-3 z-10">
        <MapStylePicker currentStyle={currentStyle} onStyleChange={handleStyleChange} />
      </div>
    </div>
  );
};

export default RouteMap;
